import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  acknowledgePolicyVersion,
  attestPolicyVersion,
  createCompliancePolicy,
  createPolicyVersion,
  editPolicyVersion,
  listCompliancePolicies,
  listPolicyAcks,
  listPolicyVersionAttestations,
  listUsers,
  publishPolicyVersion,
  updateCompliancePolicy,
  type AttestationAction,
  type PolicyStatus,
  type PolicyView,
} from "@/lib/api";
import { Modal } from "@/components/Modal";

const STATUS_STYLE: Record<PolicyStatus, { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "border border-border text-text-secondary" },
  in_review: { label: "In review", cls: "bg-accent-muted text-accent" },
  approved: { label: "Approved", cls: "bg-accent-muted text-accent" },
  published: { label: "Published", cls: "bg-success/15 text-success" },
  lapsed: { label: "Lapsed", cls: "bg-warning/15 text-warning" },
};

const NEXT_ACTION: Record<string, { action: AttestationAction; label: string } | null> = {
  none: { action: "prepared", label: "Submit for review" },
  prepared: { action: "reviewed", label: "Review" },
  reviewed: { action: "approved", label: "Approve" },
  approved: null,
};

export function CompliancePoliciesPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const { data: policies, isLoading } = useQuery({
    queryKey: ["compliance-policies"],
    queryFn: listCompliancePolicies,
  });

  const openId = params.get("open");
  const open = useMemo(
    () => policies?.find((p) => p.policy.id === openId) ?? null,
    [policies, openId],
  );

  const create = useMutation({
    mutationFn: () => createCompliancePolicy({ name: newName.trim() }),
    onSuccess: (v) => {
      setAddOpen(false);
      setNewName("");
      void qc.invalidateQueries({ queryKey: ["compliance-policies"] });
      setParams({ open: v.policy.id });
    },
  });

  return (
    <div>
      <header className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="page-title">Policies</h1>
          <p className="mt-2 text-text-secondary">
            Authored here, versioned, signed off by three people, then acknowledged by
            everyone. A published body is frozen — change means a new version.
          </p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
        >
          New policy
        </button>
      </header>

      {isLoading ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      ) : !policies || policies.length === 0 ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-center">
          <div className="max-w-sm">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-token-lg bg-accent-muted text-2xl text-accent">
              <i className="fa-solid fa-file-shield" />
            </div>
            <h2 className="font-display text-lg font-semibold text-text-heading">No policies yet</h2>
            <p className="mx-auto mt-1.5 text-sm text-text-secondary">
              Start with the Information Security Policy — GOV-01 expects it, and every
              framework requirement traces back to one.
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                <th className="px-4 py-2.5 font-semibold">Policy</th>
                <th className="px-4 py-2.5 font-semibold">Kind</th>
                <th className="px-4 py-2.5 font-semibold">Owner</th>
                <th className="px-4 py-2.5 font-semibold">Version</th>
                <th className="px-4 py-2.5 font-semibold">Acknowledged</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {policies.map((v) => (
                <tr
                  key={v.policy.id}
                  className="cursor-pointer border-b border-border-light hover:bg-bg-card-hover"
                  onClick={() => setParams({ open: v.policy.id })}
                >
                  <td className="px-4 py-2.5 font-medium text-text-heading">{v.policy.name}</td>
                  <td className="px-4 py-2.5 capitalize text-text-secondary">{v.policy.kind}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                    {v.policy.owner?.name ?? <span className="text-warning">unassigned</span>}
                  </td>
                  <td className="px-4 py-2.5 text-text-secondary">
                    {v.currentVersion
                      ? `v${v.currentVersion.version}`
                      : v.latestVersion
                        ? `v${v.latestVersion.version} (draft)`
                        : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-text-secondary">
                    {v.currentVersion ? `${v.ackCount}/${v.activeUserCount}` : "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <StatusPill status={v.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <PolicyModal view={open} onClose={() => setParams({})} />}

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="New policy"
        footer={
          <button
            onClick={() => create.mutate()}
            disabled={!newName.trim() || create.isPending}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            Create
          </button>
        }
      >
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="e.g. Information Security Policy"
          className="w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent"
          autoFocus
        />
        {create.isError && (
          <p className="mt-2 text-xs text-danger">{(create.error as Error).message}</p>
        )}
      </Modal>
    </div>
  );
}

function StatusPill({ status }: { status: PolicyStatus }) {
  const s = STATUS_STYLE[status];
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

function PolicyModal({ view, onClose }: { view: PolicyView; onClose: () => void }) {
  const qc = useQueryClient();
  const p = view.policy;
  // The version being worked on: the latest (draft or published).
  const version = view.latestVersion;

  const { data: users } = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const { data: att } = useQuery({
    queryKey: ["policy-attestations", version?.id],
    queryFn: () => listPolicyVersionAttestations(version!.id),
    enabled: !!version,
  });
  const { data: acks } = useQuery({
    queryKey: ["policy-acks", view.currentVersion?.id],
    queryFn: () => listPolicyAcks(view.currentVersion!.id),
    enabled: !!view.currentVersion,
  });

  const [body, setBody] = useState(version?.body ?? "");
  const [statement, setStatement] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setBody(version?.body ?? "");
    setError(null);
  }, [version?.id, version?.body]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["compliance-policies"] });
    void qc.invalidateQueries({ queryKey: ["compliance-overview"] });
    void qc.invalidateQueries({ queryKey: ["policy-attestations", version?.id] });
    void qc.invalidateQueries({ queryKey: ["policy-acks", view.currentVersion?.id] });
  };
  const fail = (e: Error) => setError(e.message);

  const saveBody = useMutation({
    mutationFn: () =>
      version && !version.publishedAt
        ? editPolicyVersion(version.id, body)
        : createPolicyVersion(p.id, body),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const attest = useMutation({
    mutationFn: (action: AttestationAction) => attestPolicyVersion(version!.id, action, statement),
    onSuccess: () => {
      setStatement("");
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const publish = useMutation({
    mutationFn: () => publishPolicyVersion(version!.id),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const acknowledge = useMutation({
    mutationFn: () => acknowledgePolicyVersion(view.currentVersion!.id),
    onSuccess: invalidate,
    onError: fail,
  });

  const setOwner = useMutation({
    mutationFn: (ownerId: string | null) => updateCompliancePolicy(p.id, { ownerId }),
    onSuccess: invalidate,
    onError: fail,
  });

  const bodyDirty = !!version && body !== version.body && !version.publishedAt;
  const next = version && !version.publishedAt ? NEXT_ACTION[view.latestCycleStage] : null;

  return (
    <Modal open onClose={onClose} title={p.name} wide>
      <div className="space-y-5 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <StatusPill status={view.status} />
          <span className="text-text-muted">
            {p.kind} · review every {p.reviewCadenceMonths} months
            {view.currentVersion &&
              ` · v${view.currentVersion.version} effective ${view.currentVersion.effectiveFrom}`}
          </span>
          <label className="ml-auto flex items-center gap-2 text-text-secondary">
            Owner
            <select
              value={p.ownerId ?? ""}
              onChange={(e) => setOwner.mutate(e.target.value || null)}
              className="rounded-md border border-border bg-bg-card px-2 py-1 text-sm text-text-heading"
            >
              <option value="">— unassigned —</option>
              {users
                ?.filter((u) => u.active)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </select>
          </label>
        </div>

        {view.blockers.length > 0 && (
          <ul className="space-y-1">
            {view.blockers.map((b) => (
              <li key={b.rule} className="text-warning">
                <code className="mr-2 rounded bg-bg-secondary px-1.5 py-0.5 text-xs">{b.rule}</code>
                {b.message}
              </li>
            ))}
          </ul>
        )}

        <Section
          title={
            version
              ? version.publishedAt
                ? `v${version.version} — published (frozen; save creates v${version.version + 1})`
                : `v${version.version} — draft`
              : "First draft"
          }
        >
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={14}
            placeholder="Markdown. Write what we actually do — an auditor reads this against practice."
            className="w-full rounded-token-md border border-border bg-bg px-3 py-2 font-mono text-xs text-text outline-none focus:border-accent"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={() => saveBody.mutate()}
              disabled={saveBody.isPending || !body.trim() || (!!version && body === version.body)}
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {version && !version.publishedAt ? "Save draft" : "Save as new version"}
            </button>
            {bodyDirty && (
              <span className="text-xs text-warning">
                Unsaved changes — saving resets any sign-offs on this draft.
              </span>
            )}
          </div>
        </Section>

        {version && !version.publishedAt && (
          <Section
            title={`Sign-off — ${view.latestCycleStage === "none" ? "not started" : view.latestCycleStage}${
              att ? (att.chain.valid ? " · chain verified" : " · CHAIN BROKEN") : ""
            }`}
          >
            <ul className="space-y-1.5">
              {att?.attestations.map((a) => (
                <li key={a.id} className="text-text-secondary">
                  <span className="font-medium text-text-heading">{a.actor?.name ?? "?"}</span>{" "}
                  {a.action} · {new Date(a.occurredAt).toLocaleString()}
                  {a.statement && (
                    <div className="ml-4 text-xs italic text-text-muted">“{a.statement}”</div>
                  )}
                </li>
              ))}
            </ul>
            {next && (
              <div className="mt-3 flex gap-2">
                <input
                  value={statement}
                  onChange={(e) => setStatement(e.target.value)}
                  placeholder="What are you asserting? (required)"
                  className="min-w-0 flex-1 rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text-heading placeholder:text-text-muted"
                />
                <button
                  onClick={() => attest.mutate(next.action)}
                  disabled={!statement.trim() || attest.isPending || bodyDirty}
                  className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {next.label}
                </button>
              </div>
            )}
            {view.latestCycleStage === "approved" && (
              <button
                onClick={() => publish.mutate()}
                disabled={publish.isPending}
                className="mt-3 rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                Publish v{version.version}
              </button>
            )}
          </Section>
        )}

        {view.currentVersion && (
          <Section title={`Acknowledgements — ${view.ackCount}/${view.activeUserCount}`}>
            <ul className="space-y-1">
              {acks?.map((a) => (
                <li key={a.id} className="text-text-secondary">
                  <i className="fa-solid fa-circle-check mr-2 text-success" />
                  {a.user?.name ?? "?"} · {new Date(a.acknowledgedAt).toLocaleDateString()}
                </li>
              ))}
            </ul>
            {!view.acknowledgedByMe && (
              <button
                onClick={() => acknowledge.mutate()}
                disabled={acknowledge.isPending}
                className="mt-3 rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                I have read v{view.currentVersion.version} and will follow it
              </button>
            )}
          </Section>
        )}

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
        {title}
      </h3>
      {children}
    </div>
  );
}
